import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { ArrowLeft, Truck, Ruler, CheckCircle, Mail, Ticket, X as XIcon } from 'lucide-react';
import { bevApi } from '../lib/bevApi';

const money = (v) => `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const BevsProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [step, setStep] = useState('view'); // view | checkout | success
  const [customer, setCustomer] = useState({
    name: '', email: '', phone: '',
    street: '', city: '', state: 'MO', zip: '', notes: ''
  });
  const [formError, setFormError] = useState('');
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [inquiryMsg, setInquiryMsg] = useState('');
  const [inquirySent, setInquirySent] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null); // { code, discount_amount, description }
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);

  useEffect(() => {
    Promise.all([
      bevApi.getProduct(id),
      bevApi.paypalConfig(),
    ]).then(([p, cfg]) => {
      setProduct(p);
      setPaypalCfg(cfg);
    }).catch(() => {
      navigate('/furniture');
    });
  }, [id, navigate]);

  if (!product) {
    return <div className="bev-page"><div className="bev-container" style={{ padding: '160px 0' }}>Loading…</div></div>;
  }

  const shipping = paypalCfg?.shipping_fee ?? 275;
  const subtotal = product.price;
  const discount = coupon?.discount_amount || 0;
  const total = subtotal - discount + shipping;

  const applyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponChecking(true);
    setCouponError('');
    try {
      const res = await bevApi.validateCoupon(couponInput, product.id);
      if (res.valid) {
        setCoupon(res);
        setCouponError('');
      } else {
        setCoupon(null);
        setCouponError(res.reason || 'Invalid code');
      }
    } catch {
      setCouponError('Could not check that code. Please try again.');
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const validateCheckout = () => {
    for (const key of ['name', 'email', 'phone', 'street', 'city', 'zip']) {
      if (!customer[key] || String(customer[key]).trim() === '') {
        setFormError('Please fill out all shipping fields (marked with *).');
        return false;
      }
    }
    if (!/^\S+@\S+\.\S+$/.test(customer.email)) {
      setFormError('Please provide a valid email address.');
      return false;
    }
    setFormError('');
    return true;
  };

  const handleInquiry = async (e) => {
    e.preventDefault();
    try {
      await bevApi.createInquiry({
        product_id: product.id,
        name: customer.name || 'Website Visitor',
        email: customer.email || 'no-email@provided.com',
        phone: customer.phone || '',
        message: inquiryMsg,
      });
      setInquirySent(true);
    } catch {
      setFormError('Sorry, we could not send that. Please email romero.sumpter@gmail.com directly.');
    }
  };

  return (
    <div className="bev-page bev-detail-page" data-testid="bev-detail-page">
      <div className="bev-container">
        <Link to="/furniture" className="bev-back" data-testid="bev-back-link">
          <ArrowLeft size={16} /> Back to all furniture
        </Link>

        <div className="bev-detail-grid">
          {/* Images */}
          <div className="bev-detail-imgs">
            <div className="bev-detail-main-img">
              {product.images && product.images.length > 0 ? (
                <img src={product.images[activeImg]} alt={product.name} />
              ) : (
                <div className="bev-card-placeholder">No image</div>
              )}
              {product.on_sale && product.sale_label && (
                <span className="bev-card-sale bev-card-sale-large">{product.sale_label}</span>
              )}
            </div>
            {product.images && product.images.length > 1 && (
              <div className="bev-detail-thumbs">
                {product.images.map((src, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`bev-detail-thumb ${i === activeImg ? 'is-active' : ''}`}
                  >
                    <img src={src} alt="" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info + Checkout */}
          <div className="bev-detail-info">
            <p className="bev-detail-cat">{product.subcategory || product.category}</p>
            <h1 className="bev-detail-title">{product.name}</h1>

            <div className="bev-detail-price-row">
              {product.on_sale && product.original_price && (
                <span className="bev-detail-original">{money(product.original_price)}</span>
              )}
              <span className="bev-detail-price">{money(product.price)}</span>
            </div>

            {product.dimensions && (
              <p className="bev-detail-dim"><Ruler size={14} /> {product.dimensions}</p>
            )}

            <p className="bev-detail-desc">{product.description}</p>

            <div className="bev-detail-shipping">
              <Truck size={16} />
              <div>
                <strong>St. Louis Metro delivery only</strong>
                <span>Flat $275 delivery fee per address</span>
              </div>
            </div>

            {step === 'success' ? (
              <div className="bev-checkout-success" data-testid="bev-checkout-success">
                <CheckCircle size={32} className="bev-success-icon" />
                <h3>Thank you, {customer.name.split(' ')[0]}!</h3>
                <p>Your order for <strong>{product.name}</strong> is confirmed.</p>
                <p>A copy of your order (ID: <code>{orderId?.slice(0, 8)}</code>) has been recorded. Bev will reach out shortly to coordinate delivery.</p>
                <Link to="/furniture" className="bev-btn-primary">Continue shopping</Link>
              </div>
            ) : product.status !== 'available' ? (
              <div className="bev-unavailable">
                <p><strong>{product.status === 'sold_out' ? 'Sold Out' : 'Coming Soon'}</strong></p>
                <button onClick={() => setInquiryOpen(true)} className="bev-btn-secondary" data-testid="bev-inquiry-btn">
                  <Mail size={14} /> Ask about restock
                </button>
              </div>
            ) : step === 'view' ? (
              <div className="bev-cta-stack">
                <div className="bev-total-preview">
                  <div className="bev-total-line">
                    <span>Subtotal</span><span>{money(subtotal)}</span>
                  </div>
                  <div className="bev-total-line">
                    <span>Delivery (St. Louis Metro)</span><span>{money(shipping)}</span>
                  </div>
                  <div className="bev-total-line bev-total-final">
                    <span>Total</span><span>{money(total)}</span>
                  </div>
                </div>
                <button
                  className="bev-btn-primary bev-btn-large"
                  onClick={() => setStep('checkout')}
                  data-testid="bev-buy-btn"
                >
                  Buy Now — {money(total)}
                </button>
                <button
                  className="bev-btn-ghost"
                  onClick={() => setInquiryOpen(true)}
                  data-testid="bev-ask-btn"
                >
                  <Mail size={14} /> Ask a question about this piece
                </button>
              </div>
            ) : (
              <div className="bev-checkout" data-testid="bev-checkout-form">
                <h3>Delivery details</h3>
                <p className="bev-checkout-hint">We deliver only to the St. Louis Metro area. Flat $275 delivery fee per address.</p>
                <div className="bev-form-grid">
                  <label>
                    <span>Full name *</span>
                    <input type="text" value={customer.name} onChange={e => setCustomer({...customer, name: e.target.value})} data-testid="bev-input-name" />
                  </label>
                  <label>
                    <span>Email *</span>
                    <input type="email" value={customer.email} onChange={e => setCustomer({...customer, email: e.target.value})} data-testid="bev-input-email" />
                  </label>
                  <label>
                    <span>Phone *</span>
                    <input type="tel" value={customer.phone} onChange={e => setCustomer({...customer, phone: e.target.value})} data-testid="bev-input-phone" />
                  </label>
                  <label className="bev-form-full">
                    <span>Street address *</span>
                    <input type="text" value={customer.street} onChange={e => setCustomer({...customer, street: e.target.value})} data-testid="bev-input-street" />
                  </label>
                  <label>
                    <span>City *</span>
                    <input type="text" value={customer.city} onChange={e => setCustomer({...customer, city: e.target.value})} data-testid="bev-input-city" />
                  </label>
                  <label>
                    <span>State</span>
                    <input type="text" value={customer.state} onChange={e => setCustomer({...customer, state: e.target.value})} data-testid="bev-input-state" />
                  </label>
                  <label>
                    <span>ZIP *</span>
                    <input type="text" value={customer.zip} onChange={e => setCustomer({...customer, zip: e.target.value})} data-testid="bev-input-zip" />
                  </label>
                  <label className="bev-form-full">
                    <span>Delivery notes (optional)</span>
                    <textarea rows="2" value={customer.notes} onChange={e => setCustomer({...customer, notes: e.target.value})} />
                  </label>
                </div>
                {formError && <p className="bev-form-err">{formError}</p>}

                {/* Coupon input */}
                <div className="bev-coupon-block" data-testid="bev-coupon-block">
                  {!coupon ? (
                    <div className="bev-coupon-input-row">
                      <div className="bev-coupon-input-wrap">
                        <Ticket size={14} className="bev-coupon-icon" />
                        <input
                          type="text"
                          placeholder="Have a coupon code?"
                          value={couponInput}
                          onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                          onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), applyCoupon())}
                          className="bev-coupon-input"
                          data-testid="bev-coupon-input"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponChecking || !couponInput.trim()}
                        className="bev-btn-secondary bev-coupon-apply"
                        data-testid="bev-coupon-apply"
                      >
                        {couponChecking ? 'Checking...' : 'Apply'}
                      </button>
                    </div>
                  ) : (
                    <div className="bev-coupon-applied" data-testid="bev-coupon-applied">
                      <div className="bev-coupon-applied-info">
                        <Ticket size={14} className="bev-coupon-icon" />
                        <div>
                          <strong>{coupon.code}</strong>
                          <span>{coupon.description || `${coupon.discount_type === 'percent' ? coupon.discount_value + '% off' : '$' + coupon.discount_value + ' off'}`}</span>
                        </div>
                      </div>
                      <button type="button" onClick={removeCoupon} className="bev-coupon-remove" aria-label="Remove coupon">
                        <XIcon size={14} />
                      </button>
                    </div>
                  )}
                  {couponError && <p className="bev-form-err" style={{ marginTop: 8 }}>{couponError}</p>}
                </div>

                <div className="bev-total-preview">
                  <div className="bev-total-line">
                    <span>{product.name}</span><span>{money(subtotal)}</span>
                  </div>
                  {discount > 0 && (
                    <div className="bev-total-line bev-total-discount">
                      <span>Coupon ({coupon.code})</span><span>−{money(discount)}</span>
                    </div>
                  )}
                  <div className="bev-total-line">
                    <span>Delivery</span><span>{money(shipping)}</span>
                  </div>
                  <div className="bev-total-line bev-total-final">
                    <span>Total</span><span>{money(total)}</span>
                  </div>
                </div>

                {paypalCfg?.client_id ? (
                  <div className="bev-paypal-wrap" data-testid="bev-paypal-wrap">
                    <PayPalScriptProvider options={{ 'client-id': paypalCfg.client_id, currency: 'USD', intent: 'capture' }}>
                      <PayPalButtons
                        style={{ layout: 'vertical', shape: 'pill', label: 'pay', color: 'gold' }}
                        forceReRender={[product.id, coupon?.code || '']}
                        createOrder={async () => {
                          if (!validateCheckout()) throw new Error('validation');
                          const res = await bevApi.createPaypalOrder(product.id, coupon?.code);
                          return res.id;
                        }}
                        onApprove={async (data) => {
                          try {
                            const cap = await bevApi.capturePaypalOrder(data.orderID);
                            const order = await bevApi.createOrder({
                              product_id: product.id,
                              customer,
                              paypal_order_id: data.orderID,
                              paypal_capture_id: cap.capture_id,
                              coupon_code: coupon?.code || null,
                            });
                            setOrderId(order.id);
                            setStep('success');
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          } catch {
                            setFormError('Something went wrong recording your order. Please contact us with your PayPal receipt.');
                          }
                        }}
                        onError={() => {
                          setFormError('PayPal encountered an error. Please try again or contact us.');
                        }}
                        onCancel={() => setFormError('Checkout cancelled. You can start again when ready.')}
                      />
                    </PayPalScriptProvider>
                  </div>
                ) : (
                  <p className="bev-form-err">PayPal is being set up. Please check back soon.</p>
                )}

                <button className="bev-btn-ghost" onClick={() => setStep('view')}>Cancel</button>
              </div>
            )}
          </div>
        </div>

        {/* Inquiry Modal */}
        {inquiryOpen && (
          <div className="bev-modal-overlay" onClick={() => setInquiryOpen(false)}>
            <div className="bev-modal" onClick={(e) => e.stopPropagation()} data-testid="bev-inquiry-modal">
              <h3>Ask about {product.name}</h3>
              {inquirySent ? (
                <div className="bev-modal-success">
                  <CheckCircle size={32} className="bev-success-icon" />
                  <p>Thanks! Your message has been sent. We&apos;ll be in touch soon.</p>
                  <button className="bev-btn-primary" onClick={() => { setInquiryOpen(false); setInquirySent(false); setInquiryMsg(''); }}>Close</button>
                </div>
              ) : (
                <form onSubmit={handleInquiry} className="bev-modal-form">
                  <label><span>Your name</span><input type="text" required value={customer.name} onChange={e => setCustomer({...customer, name: e.target.value})} /></label>
                  <label><span>Email</span><input type="email" required value={customer.email} onChange={e => setCustomer({...customer, email: e.target.value})} /></label>
                  <label><span>Phone (optional)</span><input type="tel" value={customer.phone} onChange={e => setCustomer({...customer, phone: e.target.value})} /></label>
                  <label><span>Your question</span><textarea rows="4" required value={inquiryMsg} onChange={e => setInquiryMsg(e.target.value)} /></label>
                  <div className="bev-modal-actions">
                    <button type="button" className="bev-btn-ghost" onClick={() => setInquiryOpen(false)}>Cancel</button>
                    <button type="submit" className="bev-btn-primary">Send</button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BevsProductDetailPage;
